import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Box, Typography } from "@mui/material"
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined"
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined"
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined"
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined"
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined"
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined"
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined"
import { useAuthContext } from "context/auth/authContext"
import { getCurrency } from "utils/functions"
import i18n from "i18n"

import PlanSection from "./PlanSection"
import ExpensesSection from "./ExpensesSection"
import CertificationsSection from "./CertificationsSection"
import ControlVsAvanceSection from "./ControlVsAvanceSection"
import ForecastSection from "./ForecastSection"
import InvoicingSection from "./InvoicingSection"
import CollectionsSection from "./CollectionsSection"
import OverviewTabBar from "./OverviewTabBar"
import { useProjectSummary } from "./hooks/useProjectSummary"
import {
  buildPlanSectionData,
  buildExpensesSectionData,
  buildCertificationsSectionData,
  buildInvoicingSectionData,
  buildCollectionsSectionData,
  buildControlVsAvanceSectionData,
  buildForecastSectionData,
} from "./selectors"

const OverviewComponent = ({ projectId }) => {
  const { user, getUser } = useAuthContext()
  const [tabIndex, setTabIndex] = useState(0)

  useEffect(() => {
    if (!user) {
      getUser()
    }
  }, [user, getUser])

  const { data: projectSummary, isLoading: isLoadingSummary } =
    useProjectSummary(projectId, !!projectId)
  const isLoading = isLoadingSummary

  const currency = user ? getCurrency(user["custom:currency"]) : ""
  const planSectionData = useMemo(
    () => buildPlanSectionData(projectSummary, currency),
    [projectSummary, currency],
  )
  const expensesData = useMemo(
    () => buildExpensesSectionData(projectSummary, currency),
    [projectSummary, currency],
  )
  const certificationsData = useMemo(
    () => buildCertificationsSectionData(projectSummary, currency),
    [projectSummary, currency],
  )
  const invoicingData = useMemo(
    () => buildInvoicingSectionData(projectSummary, currency),
    [projectSummary, currency],
  )
  const collectionsData = useMemo(
    () => buildCollectionsSectionData(projectSummary, currency, projectId),
    [projectSummary, currency, projectId],
  )
  const controlVsAvanceData = useMemo(
    () => buildControlVsAvanceSectionData(projectSummary, currency),
    [projectSummary, currency],
  )
  const forecastData = useMemo(
    () => buildForecastSectionData(projectSummary, currency),
    [projectSummary, currency],
  )

  const overviewTabs = useMemo(
    () => [
      {
        id: "plan",
        label: i18n.t("overview.tabPlan"),
        Icon: TrackChangesOutlinedIcon,
      },
      {
        id: "control",
        label: i18n.t("overview.tabControlVsAvance"),
        Icon: BarChartOutlinedIcon,
      },
      {
        id: "costs",
        label: i18n.t("overview.tabCosts"),
        Icon: LayersOutlinedIcon,
      },
      {
        id: "evolution",
        label: i18n.t("overview.tabEvolution"),
        Icon: TrendingUpOutlinedIcon,
      },
      {
        id: "invoicing",
        label: i18n.t("overview.tabInvoicing"),
        Icon: ReceiptOutlinedIcon,
      },
      {
        id: "collections",
        label: i18n.t("overview.tabCollections"),
        Icon: AccountBalanceWalletOutlinedIcon,
      },
      {
        id: "forecast",
        label: i18n.t("overview.tabForecast"),
        Icon: ShowChartOutlinedIcon,
      },
    ],
    [],
  )

  const handleTabChange = (_event, next) => {
    setTabIndex(next)
  }

  if (!user) return null

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            component="h1"
            sx={{
              fontWeight: "bold",
              color: "#414141",
              display: "flex",
              alignItems: "baseline",
              flexWrap: "wrap",
              columnGap: 1,
              rowGap: 0.25,
            }}
          >
            <Box component="span">{i18n.t("overview.resumeTitle")}</Box>
            <Box
              component="span"
              aria-label={i18n.t("overview.resumeTitleBetaAria")}
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "text.secondary",
                lineHeight: 1.2,
              }}
            >
              {i18n.t("overview.resumeTitleBeta")}
            </Box>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <OverviewTabBar
          tabs={overviewTabs}
          value={tabIndex}
          onChange={handleTabChange}
        />
      </Box>
      <Box>
        {[
          <PlanSection
            key="overview-panel-plan"
            data={planSectionData}
            isLoading={isLoading}
          />,
          <ControlVsAvanceSection
            key="overview-panel-control"
            data={controlVsAvanceData}
            isLoading={isLoading}
          />,
          <ExpensesSection
            key="overview-panel-expenses"
            data={expensesData}
            isLoading={isLoading}
          />,
          <CertificationsSection
            key="overview-panel-certifications"
            data={certificationsData}
            isLoading={isLoading}
          />,
          <InvoicingSection
            key="overview-panel-invoicing"
            data={invoicingData}
            isLoading={isLoading}
          />,
          <CollectionsSection
            key="overview-panel-collections"
            data={collectionsData}
            isLoading={isLoading}
          />,
          <ForecastSection
            key="overview-panel-forecast"
            data={forecastData}
            isLoading={isLoading}
          />,
        ].map((panel, i) => (
          <Box
            key={panel.key ?? `overview-tabpanel-wrap-${i}`}
            role="tabpanel"
            hidden={tabIndex !== i}
            id={`overview-tabpanel-${i}`}
            aria-labelledby={`overview-tab-${i}`}
            sx={{ display: tabIndex === i ? "block" : "none" }}
          >
            {panel}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

OverviewComponent.propTypes = {
  projectId: PropTypes.string,
}

export default OverviewComponent
